import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format, parseISO } from 'date-fns';
import {
  DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT,
  filterProfilesByMinLevelVariation,
} from '../utils/siloLevelVariationFilter';

export const generateProfileReport = async (
  profilesData,
  foods,
  companyName,
  farmName,
  barnName,
  startDate,
  endDate,
  minVariationPercent = DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT
) => {
  try {

    const allProfiles = profilesData.flat();

    if (allProfiles.length === 0) {
      console.warn('No hay datos de perfiles para generar el reporte');
      return;
    }


    const profilesBySilo = {};
    
    allProfiles.forEach(profile => {
      const siloId = profile.silo;
      if (!profilesBySilo[siloId]) {
        profilesBySilo[siloId] = [];
      }
      profilesBySilo[siloId].push(profile);
    });


    const workbook = new ExcelJS.Workbook();


    const titleStyle = {
      font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F7A1F' } },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };

    const subtitleStyle = {
      font: { size: 11 },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };

    const headerStyle = {
      font: { bold: true, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F7A1F' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    };

    const cellStyle = {
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
      }
    };

    const formatDateTime = (dateString) => {
      try {
        return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss');
      } catch {
        return dateString;
      }
    };

    const formatDate = (dateString) => {
      try {
        return format(parseISO(dateString), 'dd/MM/yyyy');
      } catch {
        return dateString;
      }
    };

    Object.keys(profilesBySilo).forEach((siloId) => {
      const siloProfiles = filterProfilesByMinLevelVariation(
        profilesBySilo[siloId],
        minVariationPercent
      ).sort((a, b) => {
        return new Date(b.datetime_fetched) - new Date(a.datetime_fetched);
      });

      const firstProfile = siloProfiles[0];
      const siloName = firstProfile.silo_name || `Silo ${siloId}`;
      
      const worksheetName = siloName.length > 31 ? siloName.substring(0, 31) : siloName;
      const worksheet = workbook.addWorksheet(worksheetName);

      worksheet.mergeCells('A1:G1');
      worksheet.getCell('A1').value = companyName;
      worksheet.getCell('A1').style = titleStyle;
      worksheet.getRow(1).height = 25;

      worksheet.mergeCells('A2:G2');
      worksheet.getCell('A2').value = `${farmName} - ${barnName}`;
      worksheet.getCell('A2').style = subtitleStyle;

      worksheet.mergeCells('A3:G3');
      worksheet.getCell('A3').value = `${formatDate(startDate)} - ${formatDate(endDate)}`;
      worksheet.getCell('A3').style = subtitleStyle;

      worksheet.addRow([]);

      const headerRow = worksheet.addRow([
        'Silo',
        'Data/Hora',
        'Peso (kg)',
        'Nivel (%)',
        'Volume (m³)',
        'Matéria Prima',
        'Densidade (kg/m³)'
      ]);
      
      headerRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.style = headerStyle;
      });
      headerRow.height = 20;

      const validProfiles = siloProfiles.filter(profile => profile.level !== null && profile.level > 0);
      
      validProfiles.forEach((profile, idx) => {
        const food = foods.find(f => f.pk === profile.food);
        const foodName = food ? food.name : 'N/A';
        const density = food ? food.density.toFixed(2) : '0.00';
        
        const row = worksheet.addRow([
          profile.silo_name || siloName,
          formatDateTime(profile.datetime_fetched),
          profile.weight ? profile.weight.toFixed(2) : '0.00',
          profile.level ? profile.level.toFixed(2) : '0.00',
          profile.volume ? profile.volume.toFixed(2) : '0.00',
          foodName,
          density
        ]);

        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.style = {
            ...cellStyle,
            fill: idx % 2 === 0 
              ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
              : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }
          };
        });
      });

      worksheet.columns = [
        { width: 40 },  
        { width: 20 },  
        { width: 15 },  
        { width: 12 },  
        { width: 15 },  
        { width: 40 },  
        { width: 18 }   
      ];
    });

    // Generar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const fileName = `Historico_Perfis_${companyName}_${farmName}_${barnName}_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
    saveAs(blob, fileName);

    console.log('Relatório gerado com sucesso');
  } catch (error) {
    console.error('Erro ao gerar relatório:', error);
    throw error;
  }
};

